import { useState, useEffect } from 'react';
import { I18nProvider, useI18n } from './i18n/I18nContext';
import { ThreeScene } from './three/ThreeScene';
import { CyberpunkLoader } from './components/CyberpunkLoader';
import { Hero } from './components/Hero';
import { ThoughtProcess } from './components/ThoughtProcess';
import { CharacterJourney } from './components/CharacterJourney';
import { SkillsSection } from './components/SkillsSection';
import { ProjectsSection } from './components/ProjectsSection';
import { ContactSection } from './components/ContactSection';
import { LanguageSwitcher } from './components/LanguageSwitcher';
import { HowlerProvider, useHowler } from './hooks/useHowler';
import Lenis from 'lenis';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

function PortfolioContent() {
  const { t } = useI18n();
  const { playSound } = useHowler();

  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t: number) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
    } as any);

    function raf(time: number) {
      lenis.raf(time);
      ScrollTrigger.update();
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);

    ScrollTrigger.refresh();

    return () => {
      lenis.destroy();
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === ' ' && e.target === document.body) {
        e.preventDefault();
        playSound('click');
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [playSound]);

  return (
    <>
      <ThreeScene />
      <LanguageSwitcher />
      <Hero />
      <ThoughtProcess />
      <CharacterJourney />
      <SkillsSection />
      <ProjectsSection />
      <ContactSection />
      <footer style={{
        padding: '2rem',
        textAlign: 'center',
        borderTop: '1px solid var(--border-color)',
        background: 'var(--bg-secondary)',
      }}>
        <p style={{ color: 'var(--fg-muted)', fontSize: '0.875rem' }}>
          © 2027 {t('footer.name') || 'JFAguirreT2027'}. {t('footer.rights')}.
        </p>
        <p style={{ color: 'var(--fg-muted)', fontSize: '0.75rem', marginTop: '0.5rem' }}>
          {t('footer.built')}
        </p>
      </footer>
    </>
  );
}

export default function App() {
  const [loaded, setLoaded] = useState(false);

  return (
    <I18nProvider>
      <HowlerProvider>
        <div style={{ minHeight: '100vh' }}>
          {!loaded && (
            <CyberpunkLoader onComplete={() => setLoaded(true)} />
          )}
          {loaded && <PortfolioContent />}
        </div>
      </HowlerProvider>
    </I18nProvider>
  );
}